/** Wire contract for workspace/user onboarding (matches backend `data.setup`). */

export const SETUP_KEY = "setup" as const

export const IDENTITY_SETUP_INCOMPLETE = "identity_setup_incomplete" as const

export type IdentitySetup = {
  required: boolean
  workspace: boolean
  user: boolean
}

const SETUP_EVENT = "identity-setup-changed" as const

export function parseIdentitySetupFromPayload(body: unknown): IdentitySetup | null {
  if (!body || typeof body !== "object") return null
  const root = body as Record<string, unknown>
  const data = root.data
  const nested =
    data && typeof data === "object"
      ? (data as Record<string, unknown>)[SETUP_KEY]
      : undefined
  const raw = nested ?? root[SETUP_KEY]
  if (!raw || typeof raw !== "object") return null
  const o = raw as Record<string, unknown>
  return {
    required: Boolean(o.required),
    workspace: Boolean(o.workspace),
    user: Boolean(o.user),
  }
}

/** Short-lived websocket JWT from ``identity/session`` bootstrap. */
export type WebsocketCredentials = {
  token: string
  expires_at: string
  workspace_id: string
  user_id: string
}

export type IdentitySwitchState = {
  active: boolean
  outerUserName?: string
  effectiveUserName?: string
  workspaceName?: string
}

/** Client bootstrap fields merged into the `session` command `data` object. */
export type SessionClientBootstrap = {
  workspace: { has_phone_system: boolean; id?: string }
  user: {
    id?: string
    displayName?: string
    isAdmin?: boolean
    isWorkspaceOwner?: boolean
    aclKeys?: string[]
  } & Record<string, unknown>
  switch?: IdentitySwitchState
  websocket?: WebsocketCredentials
}

export function parseWebsocketCredentials(raw: unknown): WebsocketCredentials | null {
  if (!raw || typeof raw !== "object") return null
  const o = raw as Record<string, unknown>
  if (typeof o.token !== "string" || !o.token) return null
  if (typeof o.expires_at !== "string" || !o.expires_at) return null
  if (typeof o.workspace_id !== "string" || !o.workspace_id) return null
  if (typeof o.user_id !== "string" || !o.user_id) return null
  return {
    token: o.token,
    expires_at: o.expires_at,
    workspace_id: o.workspace_id,
    user_id: o.user_id,
  }
}

export function parseClientBootstrapFromSessionData(data: unknown): SessionClientBootstrap | null {
  if (!data || typeof data !== "object") return null
  const d = data as Record<string, unknown>
  const ws = d.workspace
  const user = d.user
  if (!ws || typeof ws !== "object" || ws === null) return null
  const w = ws as Record<string, unknown>
  if (typeof w.has_phone_system !== "boolean") return null
  if (user === undefined || user === null || typeof user !== "object" || Array.isArray(user)) return null
  const workspace: SessionClientBootstrap["workspace"] = {
    has_phone_system: w.has_phone_system,
  }
  if (typeof w.id === "string" && w.id) {
    workspace.id = w.id
  }
  const userOut: SessionClientBootstrap["user"] = { ...(user as Record<string, unknown>) }
  if (typeof userOut.id !== "string") {
    const uid = (user as Record<string, unknown>).id
    if (typeof uid === "string" && uid) {
      userOut.id = uid
    }
  }
  const websocket = parseWebsocketCredentials(d.websocket)
  const switchRaw = d.switch
  let switchState: IdentitySwitchState | undefined
  if (switchRaw && typeof switchRaw === "object" && !Array.isArray(switchRaw)) {
    const s = switchRaw as Record<string, unknown>
    switchState = {
      active: Boolean(s.active),
      outerUserName: typeof s.outerUserName === "string" ? s.outerUserName : undefined,
      effectiveUserName: typeof s.effectiveUserName === "string" ? s.effectiveUserName : undefined,
      workspaceName: typeof s.workspaceName === "string" ? s.workspaceName : undefined,
    }
  }
  return {
    workspace,
    user: userOut,
    ...(switchState ? { switch: switchState } : {}),
    ...(websocket ? { websocket } : {}),
  }
}

/** Notify listeners (e.g. `IdentitySetupGate`) that setup flags changed. */
export function emitIdentitySetupChanged(setup: IdentitySetup): void {
  if (typeof window === "undefined") return
  window.dispatchEvent(new CustomEvent(SETUP_EVENT, { detail: setup }))
}

export function subscribeIdentitySetupChanged(handler: (setup: IdentitySetup) => void): () => void {
  if (typeof window === "undefined") return () => {}
  const listener = (ev: Event) => {
    const ce = ev as CustomEvent<IdentitySetup>
    if (ce.detail) handler(ce.detail)
  }
  window.addEventListener(SETUP_EVENT, listener)
  return () => window.removeEventListener(SETUP_EVENT, listener)
}
